/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiTarget, FiCalendar } from 'react-icons/fi';
import { GoLocation } from 'react-icons/go';
import { MdArrowDropDown } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { ENV } from '@environment';

import { autosSearchParameters } from 'action-creators/autos';
import DateRangePicker from 'components/DateRangePicker';
import BookingSelectDate, { RenderProps as BookingSelectRenderProps } from 'components/Selects/BookingSelectDate';
import BookingSelectLocation from 'components/Selects/BookingSelectLocation';
import BookingSelectTime, { RenderProps as TimePickerProps } from 'components/Selects/BookingSelectTime';
import BranchCompanySelect from 'components/Selects/BranchCompanySelect';
import TravelArrangerSelect from 'components/Selects/TravelArrangerSelect';
import TimePicker from 'components/TimePicker';
import PlacesSearch from 'containers/PlacesSearch';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { Airport } from 'models/airport';
import { VehiclePlace } from 'models/auto/vehicleSearch';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { City } from 'models/city';
import { GroupedListModel } from 'models/groupedList';
import ResponseScreen, { BackgroundTypes, IconTypes } from 'screens/components/ResponseScreen';
import { amplitudelogEvent, SearchEvent } from 'services/amplitudeService';
import { Guest } from 'services/userGuestService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { dateFormatWeekend, daysCount } from 'utils/generic';
import { dateToString } from 'utils/time';

import { mapAirportVehiclePlace } from './mapper';
import {
  Container,
  Fields,
  Field,
  Checkbox as CheckboxContainer,
  ReturnOther,
  SearchButtonContainer,
  SearchButton,
} from './styles';

export interface Props {
  defaultTraveler?: TravelArrangerUsersAllowed;
  defaultPickUpPoint?: VehiclePlace;
  defaultDropOffPoint?: VehiclePlace;
  defaultPickUpDate?: Date;
  defaultPickUpTime?: string;
  defaultDropOffDate?: Date;
  defaultDropOffTime?: string;
  defaultDiffDropOff: boolean;
  allowedTravelers: TravelArrangerUsersAllowed[];
  isLoadingAllowedTravelers: boolean;
  isHome?: boolean;
  onClose?: () => void;
  branchCompanies?: GetAllBillingCompanyResponse[];
  isLoadingBranchCompanies: boolean;
  defaultBranchCompany?: GetAllBillingCompanyResponse;
}

const travelersToGroupedModel = (travelers: TravelArrangerUsersAllowed[]): GroupedListModel[] =>
  travelers.map(
    (t) =>
      ({
        id: t.id,
        name: t.name,
        lastName: t.lastName,
        favorite: t.favorite,
        email: t.email,
        userGroupId: t.userGroupId,
        userGroupName: t.userGroupName,
        entity: t.guest ? 'guest' : 'user',
      } as GroupedListModel),
  );

const branchCompanyToGroupedModel = (branchCompany: GetAllBillingCompanyResponse): GroupedListModel => ({
  id: branchCompany.id,
  name: branchCompany.companyName,
});

const AutosSearch = ({
  defaultTraveler,
  defaultPickUpPoint,
  defaultDropOffPoint,
  defaultPickUpDate,
  defaultPickUpTime = '10:00 AM',
  defaultDropOffDate,
  defaultDropOffTime = '10:00 AM',
  defaultDiffDropOff,
  allowedTravelers,
  isLoadingAllowedTravelers,
  isHome = true,
  onClose,
  branchCompanies,
  isLoadingBranchCompanies,
  defaultBranchCompany,
}: Props) => {
  const history = useHistory();
  const location = useLocation<{ showNoResults?: boolean } | null>();
  const { t } = useStoreTranslation();
  const user = useUserAccount();
  const dispatch = useDispatch();

  const [showNoResults, setShowNoResults] = useState(location.state?.showNoResults || false);
  const [pickUpPoint, setPickUpPoint] = useState<VehiclePlace | Airport | City | undefined>(defaultPickUpPoint);
  const [dropOffPoint, setDropOffPoint] = useState<VehiclePlace | Airport | City | undefined>(defaultDropOffPoint);
  const [pickUpDate, setPickUpDate] = useState<Date | undefined>(defaultPickUpDate);
  const [pickUpTime, setPickUpTime] = useState<string>(defaultPickUpTime);
  const [dropOffDate, setDropOffDate] = useState<Date | undefined>(defaultDropOffDate);
  const [dropOffTime, setDropOffTime] = useState<string>(defaultDropOffTime);
  const [traveler, setTraveler] = useState<TravelArrangerUsersAllowed | undefined>(defaultTraveler);
  const [diffDropOff, setDiffDropOff] = useState<boolean>(defaultDiffDropOff);
  const [branchCompany, setBranchCompany] = useState<GetAllBillingCompanyResponse | undefined>(defaultBranchCompany);

  useEffect(() => {
    history.replace({ ...history.location, state: undefined });
  }, [history]);

  useEffect(() => {
    setTraveler(defaultTraveler);
  }, [defaultTraveler]);

  useEffect(() => {
    setBranchCompany(defaultBranchCompany);
  }, [defaultBranchCompany]);

  const isValid = useMemo(() => {
    const hasTraveler = !user?.isTravelArranger || (user?.isTravelArranger && traveler);
    return Boolean(pickUpPoint && dropOffPoint && hasTraveler && pickUpDate && dropOffDate);
  }, [user, traveler, pickUpPoint, dropOffPoint, pickUpDate, dropOffDate]);

  const isBranchCompanyValid = useMemo(() => {
    // TODO: Billing companies - descomentar para funcionamiento correcto
    return true;

    if (branchCompanies && branchCompanies.length > 0) {
      if (branchCompany === undefined) {
        return false;
      }
    }
    return true;
  }, [branchCompanies, branchCompany]);

  const transformedTravelerList: GroupedListModel[] = useMemo(
    () => travelersToGroupedModel(allowedTravelers),
    [allowedTravelers],
  );

  const transformedSelectedTraveler: GroupedListModel[] | undefined = useMemo(() => {
    if (!traveler) return undefined;
    return travelersToGroupedModel([traveler]);
  }, [traveler]);

  const handleChangeDateSelect = useCallback((dates: { initialDate: Date; finalDate: Date }) => {
    setPickUpDate(dates?.initialDate);
    setDropOffDate(dates?.finalDate);
  }, []);

  const transformedSelectedBranchCompany: GroupedListModel | undefined = useMemo(() => {
    if (!branchCompany) return undefined;
    return branchCompanyToGroupedModel(branchCompany);
  }, [branchCompany]);

  const handleClickSearchButton = useCallback(async () => {
    if (typeof pickUpPoint !== 'undefined' && pickUpDate && typeof dropOffPoint !== 'undefined' && dropOffDate) {
      const _pickUpPoint = mapAirportVehiclePlace(pickUpPoint);
      const _dropOffPoint = diffDropOff ? mapAirportVehiclePlace(dropOffPoint) : _pickUpPoint;

      //Amplitude
      const event: SearchEvent = {
        TRAVEL_PRODUCT_TYPE: 'CAR_RENTAL',
        TRAVEL_FROM: pickUpPoint?.code ?? '',
        TRAVEL_TO: dropOffPoint?.code ?? '',
        TRAVEL_DESTINATION_TYPE: '',
        TRAVEL_TRIP_TYPE: '',
        TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), pickUpDate).toString(),
        TRAVEL_DATE_FROM: new Date(pickUpDate?.toString() || '').toString() ?? '',
        TRAVEL_DATE_TO: new Date(dropOffDate?.toString() || '').toString() ?? '',
        TRAVEL_TRIP_LENGTH: (daysCount(pickUpDate, dropOffDate) + 1).toString(),
        TRAVEL_PASSENGERS: '1',
        TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
        SEARCHID: '',
      };

      amplitudelogEvent(amplitudeEvents.SELECT_TRAVEL_SEARCH, event, user);

      await dispatch(
        autosSearchParameters({
          diffDropOff,
          pickUpPoint: _pickUpPoint,
          pickUpDate: dateToString(pickUpDate),
          pickUpTime,
          dropOffPoint: _dropOffPoint,
          dropOffDate: dateToString(dropOffDate),
          dropOffTime,
          traveler,
          branchCompany,
        }),
      );
      if (isHome) history.push('/corporate/autos');
      else history.replace('/corporate/autos');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pickUpPoint,
    pickUpDate,
    dropOffPoint,
    dropOffDate,
    diffDropOff,
    traveler,
    pickUpTime,
    dropOffTime,
    isHome,
    dispatch,
    history,
    branchCompany,
  ]);

  const getBookingSelectValue = (place: VehiclePlace | Airport | City | undefined) => {
    if (!place) return;
    const detail = place.related_city.includes(place.country)
      ? place.related_city
      : `${place.related_city}, ${place.country}`;
    return {
      name: place.code,
      detail: detail,
    };
  };

  const transformedBranchCompanies = (): GroupedListModel[] =>
    branchCompanies ? branchCompanies?.map((b) => ({ id: b.id, name: b.companyName })) : ([] as GroupedListModel[]);

  const renderPickUpPointSelect = () => (
    <BookingSelectLocation
      label={t('autosSearch.pickUpPoint.label')}
      detail={t('autosSearch.pickUpPoint.detail')}
      value={getBookingSelectValue(pickUpPoint)}
      onChange={(value, isCity?: boolean) => {
        const _pickUpPoint = mapAirportVehiclePlace(value, isCity);
        setPickUpPoint(_pickUpPoint);
        if (!diffDropOff) setDropOffPoint(_pickUpPoint);
      }}
      icon={<FiTarget />}
      testId="auto-search-pickup-location"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <PlacesSearch
          {...selectProps}
          placeHolder={t('autosSearch.pickUpPoint.modalSearchPlaceholder')}
          isPickUp={true}
        />
      )}
    </BookingSelectLocation>
  );

  const renderDropOffPointSelect = () => (
    <BookingSelectLocation
      label={t('autosSearch.dropOffPoint.label')}
      detail={t('autosSearch.dropOffPoint.detail')}
      value={getBookingSelectValue(dropOffPoint)}
      onChange={(value, isCity?: boolean) => {
        const _dropOffPoint = mapAirportVehiclePlace(value, isCity);
        setDropOffPoint(_dropOffPoint);
      }}
      icon={<GoLocation />}
      testId="auto-search-dropoff-location"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <PlacesSearch
          {...selectProps}
          placeHolder={t('autosSearch.dropOffPoint.modalSearchPlaceholder')}
          isPickUp={false}
        />
      )}
    </BookingSelectLocation>
  );

  const renderPickUpDateSelect = () => (
    <BookingSelectDate
      label={t('autosSearch.rentDate.pickUp.label')}
      placeholder={t('autosSearch.rentDate.pickUp.placeholder')}
      value={dateFormatWeekend(pickUpDate)}
      onChange={handleChangeDateSelect}
      icon={<FiCalendar />}
      testId="auto-search-pickup-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          type="trip"
          onlyDate={false}
          currentInitialDate={pickUpDate}
          currentFinalDate={dropOffDate}
        />
      )}
    </BookingSelectDate>
  );

  const handleChangePickUpSelect = useCallback((time) => {
    setPickUpTime(time);
  }, []);

  const isPickUpEnabled = useMemo(() => pickUpDate === undefined, [pickUpDate]);
  const renderPickUpTimeSelect = () => (
    <BookingSelectTime
      label={t('autosSearch.rentDate.pickUp.placeholder')}
      value={pickUpTime}
      disabled={isPickUpEnabled}
      onConfirm={handleChangePickUpSelect}
      icon={<MdArrowDropDown />}
      testId="auto-search-pickup-time"
      title={t('autosSearch.rentDate.pickUp.modal.title')}
      button={t('autosSearch.rentDate.pickUp.modal.button')}
    >
      {(selectProps: TimePickerProps) => <TimePicker {...selectProps} />}
    </BookingSelectTime>
  );

  const renderDropOffDateSelect = () => (
    <BookingSelectDate
      label={t('autosSearch.rentDate.dropOff.label')}
      placeholder={t('autosSearch.rentDate.dropOff.placeholder')}
      value={dateFormatWeekend(dropOffDate)}
      onChange={handleChangeDateSelect}
      icon={<FiCalendar />}
      testId="auto-search-dropoff-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          type="trip"
          onlyDate={false}
          currentInitialDate={pickUpDate}
          currentFinalDate={dropOffDate}
          editingInitialDate={false}
          canEditingInitialDate={true}
        />
      )}
    </BookingSelectDate>
  );

  const handleChangeDropOffSelect = useCallback((time) => {
    setDropOffTime(time);
  }, []);

  const isDropOffEnabled = useMemo(() => dropOffDate === undefined, [dropOffDate]);
  const renderDropOffTimeSelect = () => (
    <BookingSelectTime
      label={t('autosSearch.rentDate.dropOff.placeholder')}
      value={dropOffTime}
      disabled={isDropOffEnabled}
      onConfirm={handleChangeDropOffSelect}
      icon={<MdArrowDropDown />}
      testId="auto-search-dropoff-time"
      title={t('autosSearch.rentDate.dropOff.modal.title')}
      button={t('autosSearch.rentDate.dropOff.modal.button')}
    >
      {(selectProps: TimePickerProps) => <TimePicker {...selectProps} />}
    </BookingSelectTime>
  );

  const handleTravelerSelect = (items: GroupedListModel[]) => {
    setTraveler(allowedTravelers.find((traveler) => items.find((t) => t.id === traveler.id)));
  };

  const handleBranchCompanySelect = (item: GroupedListModel) => {
    if (branchCompanies) {
      setBranchCompany(branchCompanies?.find((branchCompany) => branchCompany.id === item.id));
    }

    return;
  };

  const handleCreateGuestTraveler = (guest: Guest) => {
    setTraveler({
      guest: true,
      favorite: false,
      userId: guest.userId || '',
      id: guest.id || '',
      name: guest.name,
      lastName: guest.lastName,
      email: guest.email,
      birthDate: guest.birthDate,
      phoneNumber: guest.phoneNumber,
      country: guest.country,
      documentNumber: guest.documentNumber,
      gender: guest.gender,
      passportNumber: guest.passportNumber,
    });
  };

  const renderTravelerSelect = () => {
    if (!user) return;
    if (!user.isTravelArranger) return null;

    return (
      <TravelArrangerSelect
        title={t('travelArrangerSelector.placeholder')}
        buttonLabel={t('travelArrangerSelector.buttonLabel')}
        addGuestLabel={t('travelArrangerSelector.addGuest')}
        inviteAllowed={user.isGuestBookAllowed}
        options={transformedTravelerList}
        value={transformedSelectedTraveler}
        onChange={handleTravelerSelect}
        onCreateGuest={handleCreateGuestTraveler}
        isLoading={isLoadingAllowedTravelers}
        testId="auto-search-traveler"
        singleSelect={true}
      />
    );
  };

  const handleChangeDiff = () => {
    if (diffDropOff) setDropOffPoint(pickUpPoint);
    setDiffDropOff(!diffDropOff);
  };

  const renderBranchCompanySelect = () => {
    if (!user) return;
    if (!branchCompanies) return;
    if (branchCompanies.length <= 0) return;

    // TODO - Comentado para no mostrar, debe cambiar para BillingCompanies para no impactar Branch
    return;

    return (
      <BranchCompanySelect
        title={t('billingCompanySelector.placeholder')}
        buttonLabel={t('billingCompanySelector.buttonLabel')}
        options={transformedBranchCompanies()}
        value={transformedSelectedBranchCompany}
        onChange={handleBranchCompanySelect}
        isLoading={isLoadingBranchCompanies}
        testId="hotel-search-branch-company"
      />
    );
  };

  return (
    <>
      <Container>
        <Fields>
          <Field>{renderPickUpPointSelect()}</Field>
          <Field>
            <CheckboxContainer checked={diffDropOff} onChange={handleChangeDiff} testId="auto-search-diff-drop-off">
              <ReturnOther onClick={handleChangeDiff}>{t('autosSearch.differentDropOff')}</ReturnOther>
            </CheckboxContainer>
          </Field>
          {diffDropOff ? <Field>{renderDropOffPointSelect()}</Field> : null}
          <Field columns={2}>
            {renderPickUpDateSelect()} {renderPickUpTimeSelect()}
          </Field>
          <Field columns={2}>
            {renderDropOffDateSelect()} {renderDropOffTimeSelect()}
          </Field>
          <Field>{renderTravelerSelect()}</Field>
          <Field>{renderBranchCompanySelect()}</Field>
        </Fields>
        <SearchButtonContainer>
          <SearchButton
            disabled={!isValid || isLoadingAllowedTravelers || !isBranchCompanyValid}
            onClick={handleClickSearchButton}
            value={t('autosSearch.searchButton')}
            testId="auto-search-submit"
          />
        </SearchButtonContainer>
      </Container>

      <ResponseScreen
        show={showNoResults}
        text=""
        subText={t('autosSearch.searchError')}
        icon={IconTypes.Airplane}
        background={BackgroundTypes.Cloud}
        buttonOnClick={() => setShowNoResults(false)}
        buttonText={t('autosSearch.backButton')}
      />
    </>
  );
};
export default AutosSearch;
